import { useState, type ReactNode } from "react"
import { SlidersHorizontal } from "@phosphor-icons/react"
import { IconButton } from "~/components/reusable/Button"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { AggregationMethod, AggregationSelector } from "~/components/reusable/AggregationSelector"
import ColumnChooser from "~/components/planning/ProductTable/ColumnChooser"
import ProductTableColumnChooser from "~/components/planning/ProductTable/ProductTableColumnChooser"
import CustomerColumnChooser from "~/components/reusable/CustomerColumnChooser"
import GridOwnedColumnChooser from "~/components/planning/columnChooser/GridOwnedColumnChooser"
import ColumnChooserDialog from "~/components/planning/columnChooser/ColumnChooserDialog"
import { useOpenColumnChooserStore } from "~/components/planning/columnChooser/openColumnChooser"
import Select from "~/components/reusable/Select"
import { useTableRegistry } from "~/components/planning/TableRegistryContext"
import { getNewAggregations } from "~/utils/productTableUtils"
import { trackEvent } from "~/hooks/useTrackingService"
import { Separator } from "~/components/reusable/Separator"

type Props = {
  tableId?: string
  extraOptions?: ReactNode
}

export const DisplayOptionsPopover = ({ tableId, extraOptions }: Props) => {
  const [open, setOpen] = useState(false)
  const capabilities = useTableRegistry(tableId)
  // Capabilities only exist for a registered table, so the id is set wherever a chooser renders.
  const registeredTableId = tableId ?? ""

  const hasAggregation =
    (!!capabilities?.aggregationOptions && capabilities.aggregationOptions.length > 1) ||
    (!!capabilities?.buyGroupByProps && capabilities.buyGroupByProps.options.length > 1) ||
    (!!capabilities?.groupByLevelsProps && capabilities.groupByLevelsProps.options.length > 1)
  const hasColumns =
    !!capabilities?.columnChooserProps ||
    !!capabilities?.customerColumnChooserProps ||
    !!capabilities?.gridOwnedChooserProps ||
    !!capabilities?.columnChooserDialogProps
  const hasExtras = !!extraOptions
  // The chooser mounts only inside the open popover; it then takes the request and opens itself.
  const chooserRequested = useOpenColumnChooserStore(
    (s) => !!tableId && s.requestedTableId === tableId,
  )
  if (chooserRequested && hasColumns && !open) setOpen(true)

  if (!hasAggregation && !hasColumns && !hasExtras) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ShortcutTooltip label="Display options">
        <PopoverTrigger asChild>
          <IconButton variant="neutral" size="sm" aria-label="Display options">
            <SlidersHorizontal />
          </IconButton>
        </PopoverTrigger>
      </ShortcutTooltip>
      <PopoverContent align="end" sideOffset={8} className="w-[320px] p-3 flex flex-col gap-3">
        {!!capabilities?.aggregationOptions &&
          capabilities.aggregationOptions.length > 1 &&
          tableId && <AggregationSection tableId={tableId} capabilities={capabilities} />}
        {capabilities?.buyGroupByProps && capabilities.buyGroupByProps.options.length > 1 && (
          <div className="flex flex-row items-center justify-between gap-2">
            <span className="label-base">Group rows by</span>
            <Select
              size="sm"
              className="min-w-[150px]"
              options={capabilities.buyGroupByProps.options}
              value={
                capabilities.buyGroupByProps.options.find(
                  (o) => o.value === capabilities.buyGroupByProps!.value,
                ) ?? null
              }
              onChange={(opt) => opt && capabilities.buyGroupByProps!.onChange(opt.value)}
              isClearable={false}
            />
          </div>
        )}
        {capabilities?.groupByLevelsProps && capabilities.groupByLevelsProps.options.length > 1 && (
          <GroupByLevelsSection tableId={tableId} levels={capabilities.groupByLevelsProps} />
        )}
        {hasAggregation && hasColumns && <Separator />}
        {capabilities?.columnChooserProps && (
          <div className="flex flex-row items-center justify-between">
            <span className="label-base">Columns</span>
            <RegisteredColumnChooser
              tableId={registeredTableId}
              {...capabilities.columnChooserProps}
            />
          </div>
        )}
        {capabilities?.customerColumnChooserProps && (
          <div className="flex flex-row items-center justify-between">
            <span className="label-base">Columns</span>
            <CustomerColumnChooser
              availableColumnIds={capabilities.customerColumnChooserProps.availableColumnIds}
              displayedColumnIds={capabilities.customerColumnChooserProps.displayedColumnIds}
              setDisplayedColumnIds={capabilities.customerColumnChooserProps.setDisplayedColumnIds}
            />
          </div>
        )}
        {capabilities?.gridOwnedChooserProps && (
          <div className="flex flex-row items-center justify-between">
            <span className="label-base">Columns</span>
            <GridOwnedColumnChooser
              {...capabilities.gridOwnedChooserProps}
              tableId={registeredTableId}
            />
          </div>
        )}
        {capabilities?.columnChooserDialogProps && (
          <div className="flex flex-row items-center justify-between">
            <span className="label-base">Columns</span>
            <ColumnChooserDialog
              columnIds={capabilities.columnChooserDialogProps.columnIds}
              displayedColumnIds={capabilities.columnChooserDialogProps.displayedColumnIds}
              setDisplayedColumnIds={capabilities.columnChooserDialogProps.setDisplayedColumnIds}
              columnMetadata={capabilities.columnChooserDialogProps.columnMetadata}
              nonRemovableColumns={capabilities.columnChooserDialogProps.nonRemovableColumns}
              defaultColumnIds={capabilities.columnChooserDialogProps.defaultColumnIds}
              trackingPrefix={capabilities.columnChooserDialogProps.storageKey}
              tableId={registeredTableId}
            />
          </div>
        )}
        {hasExtras && (
          <div className="flex flex-col gap-2">
            {(hasAggregation || hasColumns) && <Separator />}
            {extraOptions}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** One "Group rows by" / "and" row: a level's select, plain enough to close over nothing. */
const groupByLevelRow = (
  label: string,
  options: { value: string; label: string }[],
  current: string | null,
  onChange: (next: string | null) => void,
  clearable: boolean,
) => (
  <div key={label} className="flex flex-row items-center justify-between gap-2">
    <span className={label === "and" ? "label-base text-fg-subtle" : "label-base"}>{label}</span>
    <Select
      size="sm"
      className="min-w-[150px]"
      options={options}
      value={options.find((o) => o.value === current) ?? null}
      onChange={(opt) => onChange(opt?.value ?? null)}
      isClearable={clearable}
      placeholder={clearable ? "None" : undefined}
    />
  </div>
)

/**
 * "Group rows by" for `groupByLevelsProps`. With `implicitLeaf`: product-table semantics (leaf always
 * innermost, no secondary for a leaf-like primary). Without: free-form clearable "and" levels.
 */
const GroupByLevelsSection = ({
  tableId,
  levels,
}: {
  tableId?: string
  levels: NonNullable<NonNullable<ReturnType<typeof useTableRegistry>>["groupByLevelsProps"]>
}) => {
  const first = levels.options[0]?.value ?? ""
  const value = levels.value.length > 0 ? levels.value : [first]
  const track = (method: string, level: number) =>
    trackEvent("Aggregation Method Changed", {
      "Aggregation Method": method,
      "Aggregation Level": level,
      "Table ID": tableId,
    })

  if (levels.implicitLeaf) {
    const leaf = levels.implicitLeaf
    const leafLike = levels.leafLike ?? [leaf]
    const primary = value[0] ?? first
    const secondary = value[1] ?? leaf
    const showSecondary = !leafLike.includes(primary)
    // As getNewAggregations: a leaf-like primary stands alone; others default to the level above the leaf.
    const stack = (p: string, s?: string) => {
      if (p === leaf) return [p]
      if (leafLike.includes(p)) return [p, leaf]
      const second = s ?? leafLike.find((l) => l !== leaf) ?? leaf
      return second === leaf ? [p, leaf] : [p, second, leaf]
    }
    return (
      <div className="flex flex-col gap-2">
        {groupByLevelRow(
          "Group rows by",
          levels.options,
          primary,
          (next) => {
            if (next == null) return
            track(next, 0)
            levels.onChange(stack(next))
          },
          false,
        )}
        {showSecondary &&
          groupByLevelRow(
            "and",
            levels.options.filter((o) => o.value !== primary),
            secondary,
            (next) => {
              if (next == null) return
              track(next, 1)
              levels.onChange(stack(primary, next))
            },
            false,
          )}
      </div>
    )
  }

  const maxLevels = levels.maxLevels ?? 3
  const rows = value.length < maxLevels ? [...value, null] : value
  const setLevel = (index: number, next: string | null) => {
    track(next ?? "none", index)
    // Clearing a level drops everything beneath it.
    const kept = next == null ? value.slice(0, index) : [...value.slice(0, index), next]
    const deduped = kept.filter((v, i) => kept.indexOf(v) === i)
    levels.onChange(deduped.length > 0 ? deduped : [first])
  }
  return (
    <div className="flex flex-col gap-2">
      {rows.map((current, index) => {
        const used = new Set(value.slice(0, index))
        return groupByLevelRow(
          index === 0 ? "Group rows by" : "and",
          levels.options.filter((o) => !used.has(o.value)),
          current,
          (next) => setLevel(index, next),
          index > 0,
        )
      })}
    </div>
  )
}

const AggregationSection = ({
  tableId,
  capabilities,
}: {
  tableId: string
  capabilities: NonNullable<ReturnType<typeof useTableRegistry>>
}) => {
  const setAggregations = capabilities.setAggregations
  const options = capabilities.aggregationOptions ?? []
  const meta = capabilities.aggregationMeta
  const aggregations = capabilities.aggregationValue ?? []

  if (!setAggregations) return null

  const primary = (aggregations[0] ?? options[0]) as AggregationMethod
  const secondary = aggregations[1] as AggregationMethod | undefined
  const secondaryOptions = options.filter((agg) => agg !== primary)

  const showSecondary =
    aggregations.length > 1 &&
    primary !== AggregationMethod.ProductName &&
    primary !== AggregationMethod.Variant

  const handleChange = (method: AggregationMethod | string, level: 0 | 1) => {
    trackEvent("Aggregation Method Changed", {
      "Aggregation Method": method,
      "Aggregation Level": level,
      "Table ID": tableId,
    })
    if (level === 0) {
      setAggregations(getNewAggregations(method as AggregationMethod))
      return
    }
    if (!aggregations[0]) return
    if (method === AggregationMethod.Variant) {
      setAggregations([aggregations[0], method as AggregationMethod])
    } else {
      setAggregations([aggregations[0], method as AggregationMethod, AggregationMethod.Variant])
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="label-base">Group rows by</span>
        <AggregationSelector
          prefix=""
          menuAlign="right"
          selectedMethod={primary}
          aggregationMethods={options}
          dynamicAggregationMethods={meta?.extraDataFields}
          collectionDynamicAggregationMethods={
            meta?.hasCollectionContext ? meta?.collectionExtraDataFields : undefined
          }
          onSelect={(m) => handleChange(m, 0)}
        />
      </div>
      {showSecondary && secondary && (
        <div className="flex flex-row items-center justify-between gap-2">
          <span className="label-base text-fg-subtle">and</span>
          <AggregationSelector
            prefix=""
            menuAlign="right"
            selectedMethod={secondary}
            aggregationMethods={secondaryOptions}
            dynamicAggregationMethods={meta?.extraDataFields}
            collectionDynamicAggregationMethods={
              meta?.hasCollectionContext ? meta?.collectionExtraDataFields : undefined
            }
            onSelect={(m) => handleChange(m, 1)}
          />
        </div>
      )}
    </div>
  )
}

function RegisteredColumnChooser(
  props: NonNullable<NonNullable<ReturnType<typeof useTableRegistry>>["columnChooserProps"]> & {
    tableId: string
  },
) {
  if (props.owner === "grid") {
    const { owner: _owner, ...chooserProps } = props
    return <ProductTableColumnChooser {...chooserProps} />
  }
  const { owner: _owner, ...chooserProps } = props
  return <ColumnChooser {...chooserProps} />
}
